import { defineStore } from "pinia";
import type { NotReadyReport } from "~/types/services";
import { notReadyReportsUrl } from "~/services/const";

async function fetchPreparingReports(): Promise<NotReadyReport[]> {
  const { data, error } = await useSFetch<NotReadyReport[]>(notReadyReportsUrl);
  if (error.value) {
    console.error("Error fetching not ready reports:", error.value);
  } else if (data.value) {
    return data.value;
  }
  return [];
}

type StateShape = {
  preparingReports: NotReadyReport[];
  preparedReports: NotReadyReport[];
  isDialogOpen: boolean;
  loading: boolean;
  _pollingTimer: number | null;
};

const POLL_INTERVAL = 20_000;

export const usePreparingReportStore = defineStore("preparingReportStore", {
  state: (): StateShape => ({
    preparingReports: [],
    preparedReports: [],
    isDialogOpen: false,
    loading: true,
    _pollingTimer: null,
  }),

  actions: {
    getReadyReports(newReports: NotReadyReport[]): NotReadyReport[] {
      return this.preparingReports.filter(
        (report) => !newReports.some((newReport) => newReport.id === report.id)
      );
    },

    appendReadyReports(readyReports: NotReadyReport[]): void {
      this.preparedReports.push(...readyReports);
    },

    setPreparingReports(reports: NotReadyReport[]): void {
      this.preparingReports = reports;
    },

    setDialogState(isOpen: boolean): void {
      this.isDialogOpen = isOpen;
    },

    removePreparedReport(reportId: number): void {
      this.preparedReports = this.preparedReports.filter(
        (report) => report.id !== reportId
      );
    },

    updateReportsState(newReports: NotReadyReport[]): void {
      const readyReports = this.getReadyReports(newReports);
      this.appendReadyReports(readyReports);
      this.setPreparingReports(newReports);
    },

    async fetchPreparingReports(): Promise<NotReadyReport[]> {
      try {
        const data = await fetchPreparingReports();
        this.updateReportsState(data);
        return data;
      } catch (err) {
        console.error("[preparingReportStore] fetch error:", err);
        return [];
      }
    },

    clearPolling(): void {
      if (this._pollingTimer) {
        clearInterval(this._pollingTimer);
        this._pollingTimer = null;
      }
    },

    async startPollingReports(): Promise<void> {
      this.loading = true;
      const data = await this.fetchPreparingReports();
      this.loading = false;

      // stop polling if empty
      if (data.length === 0) {
        this.clearPolling();
        return;
      }

      // clear old timer if exists
      this.clearPolling();

      this._pollingTimer = setInterval(async () => {
        const polledData = await this.fetchPreparingReports();
        if (polledData.length === 0) {
          this.clearPolling();
        }
      }, POLL_INTERVAL) as unknown as number;
    },

    /** Immediately fetch and restart the interval countdown */
    async forcePoll(): Promise<void> {
      this.clearPolling();

      const data = await this.fetchPreparingReports();

      // stop polling if empty
      if (data.length === 0) {
        return;
      }

      this._pollingTimer = setInterval(async () => {
        const polledData = await this.fetchPreparingReports();
        if (polledData.length === 0) {
          this.clearPolling();
        }
      }, POLL_INTERVAL) as unknown as number;
    },
  },
});
